/**
 * The Report dialog (Console v2 §3.3), as Discord's: pick what is wrong
 * from a list, then the second step when the category has one, an optional
 * note (and "Also block" for a person), then "Thanks for telling us. Our
 * Safety team will review it." "Breaks this server's rules" is offered only
 * for something in a server. A self-harm report shows a calm line about
 * local emergency services (no helpline numbers: none are verified).
 */
import {
  REPORT_CATEGORIES,
  REPORT_NOTE_MAX,
  Routes,
  reportCategory,
  type CreateReportRequest,
  type CreateReportResponse,
  type ReportCategory,
} from '@neosocial/protocol';
import { useState } from 'react';
import { blockUser } from '../hub/social';
import { useT } from '../i18n';
import { api, isApiRequestError } from '../lib/api';
import { errorText } from '../mod/actions';
import { displayName } from '../store/selectors';
import { Btn, Icon } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import { closeReport, useReport, type ReportTarget } from './openReport';
import './report.css';

type Step = 'category' | 'sub' | 'details' | 'done';

/** The server a report is about or happened in, if any ("Breaks this server's rules"). */
function guildOf(t: ReportTarget): string | null {
  if (t.kind === 'guild') return t.guildId;
  if (t.kind === 'message') return t.message.guild_id ?? null;
  return t.guildId ?? null;
}

function requestOf(
  t: ReportTarget,
  category: ReportCategory,
  sub: string | null,
  note: string,
): CreateReportRequest {
  const base = {
    category,
    ...(sub ? { subcategory: sub } : {}),
    ...(note.trim() ? { note: note.trim() } : {}),
  };
  if (t.kind === 'message') {
    return {
      subject_type: 'message',
      message_id: t.message.id,
      channel_id: t.message.channel_id,
      ...base,
    };
  }
  if (t.kind === 'user') {
    return {
      subject_type: 'user',
      user_id: t.user.id,
      ...(t.guildId ? { guild_id: t.guildId } : {}),
      ...base,
    };
  }
  return { subject_type: 'guild', guild_id: t.guildId, ...base };
}

/** Mounted once (shell/StaffNotices): the dialog `openReport` asks for. */
export function ReportHost() {
  const target = useReport((s) => s.target);
  const seq = useReport((s) => s.seq);
  if (!target) return null;
  return <ReportDialog key={seq} target={target} onClose={closeReport} />;
}

export function ReportDialog({ target, onClose }: { target: ReportTarget; onClose(): void }) {
  const { t } = useT();
  const [step, setStep] = useState<Step>('category');
  const [category, setCategory] = useState<ReportCategory | null>(null);
  const [sub, setSub] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [block, setBlock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inGuild = !!guildOf(target);
  const cats = REPORT_CATEGORIES.filter((c) => !c.guildOnly || inGuild);
  const def = category ? reportCategory(category) : null;
  const person =
    target.kind === 'user'
      ? displayName(target.user)
      : target.kind === 'message'
        ? displayName(target.message.author)
        : '';
  const go = (next: Step) => {
    setErr(null);
    setStep(next);
  };
  const pick = (c: ReportCategory) => {
    setCategory(c);
    setSub(null);
    go(reportCategory(c)!.subcategories.length ? 'sub' : 'details');
  };
  const send = async () => {
    if (!category) return;
    setBusy(true);
    setErr(null);
    try {
      await api.client.post<CreateReportResponse>(
        Routes.reports(),
        requestOf(target, category, sub, note),
      );
      if (block && target.kind === 'user') await blockUser(target.user.id);
      setStep('done');
    } catch (e) {
      setErr(isApiRequestError(e) && e.status === 429 ? t('rp.tooMany') : errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const title =
    step === 'done'
      ? t('rp.thanksTitle')
      : target.kind === 'message'
        ? t('rp.reportMessage')
        : target.kind === 'user'
          ? t('rp.reportUser')
          : t('rp.reportServer');
  const back =
    step === 'sub'
      ? 'category'
      : step === 'details'
        ? def?.subcategories.length
          ? 'sub'
          : 'category'
        : null;
  return (
    <Dialog
      title={title}
      icon={step === 'done' ? 'check-circle' : 'flag'}
      tone={step === 'done' ? 'mode' : 'danger'}
      size="md"
      cls="ns-rp"
      onClose={onClose}
      actions={
        step === 'done' ? (
          <Btn kind="primary" size="sm" onClick={onClose}>
            {t('c.done')}
          </Btn>
        ) : (
          <>
            {back ? (
              <Btn kind="ghost" size="sm" onClick={() => go(back)}>
                {t('c.back')}
              </Btn>
            ) : (
              <Btn kind="ghost" size="sm" onClick={onClose}>
                {t('c.cancel')}
              </Btn>
            )}
            {step === 'details' ? (
              <Btn kind="danger" size="sm" busy={busy} onClick={() => void send()}>
                {t('rp.send')}
              </Btn>
            ) : null}
          </>
        )
      }
    >
      {step === 'done' ? (
        <div className="ns-rp-done">
          <p>{t('rp.thanks')}</p>
          {category === 'self_harm' ? <p className="ns-rp-calm">{t('rp.selfHarmHelp')}</p> : null}
          {block ? <p className="ns-help">{t('rp.blocked', { name: person })}</p> : null}
        </div>
      ) : null}
      {step !== 'done' && target.kind === 'message' ? (
        <blockquote className="ns-rp-quote" dir="auto">
          <b>{displayName(target.message.author)}</b>
          <span>
            {target.message.content.slice(0, 280) ||
              t('rp.attachmentsOnly', { n: target.message.attachments.length })}
          </span>
        </blockquote>
      ) : null}
      {step === 'category' ? (
        <>
          <p className="ns-help">{t('rp.whatsWrong')}</p>
          <div className="ns-rp-list" role="radiogroup" aria-label={t('rp.whatsWrong')}>
            {cats.map((c) => (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={category === c.id}
                className="ns-rp-opt"
                onClick={() => pick(c.id)}
              >
                <span>{t(`rp.cat.${c.id}`)}</span>
                <Icon name="chev-right" />
              </button>
            ))}
          </div>
        </>
      ) : null}
      {step === 'sub' && def ? (
        <>
          <p className="ns-help">{t(`rp.cat.${def.id}`)}</p>
          {def.id === 'self_harm' ? <p className="ns-rp-calm">{t('rp.selfHarmHelp')}</p> : null}
          <div className="ns-rp-list" role="radiogroup" aria-label={t(`rp.cat.${def.id}`)}>
            {def.subcategories.map((s) => (
              <button
                key={s.id}
                type="button"
                role="radio"
                aria-checked={sub === s.id}
                className="ns-rp-opt"
                onClick={() => {
                  setSub(s.id);
                  go('details');
                }}
              >
                <span>{t(`rp.sub.${s.id}`)}</span>
                <Icon name="chev-right" />
              </button>
            ))}
          </div>
        </>
      ) : null}
      {step === 'details' && category ? (
        <div className="ns-rp-details">
          <p className="ns-rp-picked">
            <Icon name="flag" />
            <span>
              {t(`rp.cat.${category}`)}
              {sub ? ` · ${t(`rp.sub.${sub}`)}` : ''}
            </span>
          </p>
          {category === 'self_harm' ? <p className="ns-rp-calm">{t('rp.selfHarmHelp')}</p> : null}
          <label className="ns-field">
            <span className="ns-label">{t('rp.note')}</span>
            <textarea
              className="nx-textarea"
              dir="auto"
              rows={3}
              maxLength={REPORT_NOTE_MAX}
              value={note}
              placeholder={t('rp.notePlaceholder')}
              onChange={(e) => setNote(e.target.value)}
            />
            <span className="ns-help">
              {t('rp.noteCount', { n: note.length, max: REPORT_NOTE_MAX })}
            </span>
          </label>
          {target.kind === 'user' ? (
            <label className="ns-check">
              <input type="checkbox" checked={block} onChange={(e) => setBlock(e.target.checked)} />
              <span>{t('rp.alsoBlock', { name: person })}</span>
            </label>
          ) : null}
          <p className="ns-help">{t('rp.private')}</p>
          {err ? (
            <p className="ns-err" role="alert">
              {err}
            </p>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
